import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { api, bgStyle, errMsg } from "@/lib/api";

const clamp = (v) => Math.min(1, Math.max(0, v));

export const FocusDialog = ({ open, onOpenChange, photo, aspect, onSaved, testId }) => {
  const [f, setF] = useState({ fx: photo.fx, fy: photo.fy, zoom: photo.zoom });
  const box = useRef(null);
  const drag = useRef(null);

  useEffect(() => { if (open) setF({ fx: photo.fx, fy: photo.fy, zoom: photo.zoom }); }, [open, photo]);

  const onDown = (e) => { drag.current = { x: e.clientX, y: e.clientY, ...f }; e.currentTarget.setPointerCapture(e.pointerId); };
  const onMove = (e) => {
    if (!drag.current) return;
    const { width: bw, height: bh } = box.current.getBoundingClientRect();
    const s = Math.max(bw / photo.width, bh / photo.height) * f.zoom;
    const overX = photo.width * s - bw, overY = photo.height * s - bh;
    setF((cur) => ({
      ...cur,
      fx: overX > 0.5 ? clamp(drag.current.fx - (e.clientX - drag.current.x) / overX) : 0.5,
      fy: overY > 0.5 ? clamp(drag.current.fy - (e.clientY - drag.current.y) / overY) : 0.5,
    }));
  };

  const save = async () => {
    try {
      const { data } = await api.patch(`/photos/${photo.id}`, f);
      onSaved(data); onOpenChange(false);
      toast.success("Encuadre guardado");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`focus-dialog-${testId}`} className="max-w-2xl bg-[#FDFBF7]">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-normal">Ajustar encuadre</DialogTitle>
          <DialogDescription>Arrastra la foto para elegir qué parte se ve en la placa. La foto no se modifica, solo se recorta.</DialogDescription>
        </DialogHeader>
        <div className="mx-auto w-full" style={{ maxWidth: aspect >= 1 ? "100%" : `${aspect * 420}px` }}>
          <div ref={box} data-testid={`focus-area-${testId}`} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={() => (drag.current = null)}
            className="relative w-full cursor-grab touch-none overflow-hidden rounded-[4px] active:cursor-grabbing shadow-[0_20px_40px_-20px_rgba(28,23,21,0.5)]"
            style={{ aspectRatio: aspect, ...bgStyle({ ...photo, ...f }, aspect) }} />
        </div>
        <div className="flex items-center gap-4 pt-2">
          <span className="eyebrow">Zoom</span>
          <Slider data-testid={`focus-zoom-${testId}`} min={1} max={3} step={0.01} value={[f.zoom]} onValueChange={([z]) => setF((c) => ({ ...c, zoom: z }))} />
          <span className="w-12 text-right text-xs text-[#5C534C]">{f.zoom.toFixed(2)}×</span>
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="ghost" data-testid={`focus-reset-${testId}`} onClick={() => setF({ fx: 0.5, fy: 0.5, zoom: 1 })}>Centrar</Button>
          <Button data-testid={`focus-save-${testId}`} onClick={save} className="rounded-full px-8">Guardar encuadre</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
